<template>
    <div class="modal-overlay">
        <div class="modal-content max-width-600">
            <div class="panel-title">
                <span>
                    <i class="fas fa-qrcode"></i>
                    <span :class="{ 'text-danger': ratios.warning }"> {{ $t('stocks.information') }}</span>
                </span>
                <span @click="closePanel"><i class="cursor-pointer ti-close right-side-toggle medium-icon"></i></span>
            </div>
            <loading-container :loading="form.stockInfoLoading">
                <div class="card modal-body">
                    <div class="card-body">
                        <div class="mt-2">
                            <span class="row-align">
                                <strong>{{ ratios.code }}</strong>
                                <span>
                                    {{ ratios.name }}
                                </span>
                            </span>
                            <span class="row-align">
                                <small class="text-muted">{{ $t('stocks.description') }}</small>
                                <span>{{ formattedDescription() }}
                                    <button class="btn btn-xs p-0" v-if="ratios.desc.length > 100"
                                        @click="() => collapseSidebar('toggleDes')">
                                        {{ form.toggleDes ? $t('common.view_less') : $t('common.view_more') }}
                                    </button>
                                </span>
                            </span>
                            <span class="row-align">
                                <small class="text-muted">{{ $t('stocks.sector') }} - {{ $t('stocks.industry')}}</small>
                                <span>
                                    <IconTooltip :iconClass="getSectorIcons(ratios.sector)"
                                        :text="getSectorText(ratios.sector)" />
                                    <span class="hidden-sm-up"> {{ getSectorText(ratios.sector) }}</span>
                                    <span class="hidden-xs-down"> {{ getSectorText(ratios.sector) }}</span>
                                    - {{ ratios.industry }}
                                </span>
                            </span>
                            <span class="row-align">
                                <small class="text-muted">{{ $t('business_types.title') }}</small>
                                <span>
                                    <IconTooltip :iconClass="getTypeIcons(ratios.type)"
                                        :text="getBusinessTypeText(ratios.type)" />
                                    <span class="hidden-sm-up"> {{ getBusinessTypeText(ratios.type) }}</span>
                                    <span class="hidden-xs-down"> {{ getBusinessTypeText(ratios.type) }}</span>
                                </span>
                            </span>
                            <span class="row-align">
                                <small class="text-muted">{{ $t('stocks.no_of_shares') }}</small>
                                <h6>{{ ratios.shares | numberDisplay }}</h6>
                            </span>
                            <div class="row-align">
                                <tooltip :isText="$t('stocks.cash_per_shares.title')"
                                    :msg="$t('stocks.cash_per_shares.tip')" />
                                <h6>{{ ratios.v_cash_per_shares.current_value | numberDisplay }}
                                    <span class="currencyFlag">{{ $t('currency') }}
                                        <!-- <change-status v-if="ratios.v_cash_per_shares" :status="ratios.v_cash_per_shares"
                                            :unit="4" /> -->
                                    </span>
                                </h6>
                            </div>
                            <span class="row-align">
                                <div class="row-align">
                                    <tooltip :isText="$t('stocks.current_pb')" :msg="$t('stocks.pb.tip')" />
                                    <span>
                                        <index-status :status="ratios.r_pb"
                                            :value="ratios.current_PB | numberDisplayFloat(3)" />
                                    </span>
                                </div>
                                <div class="row-align">
                                    <tooltip :isText="$t('stocks.pb.title')" :msg="$t('stocks.pb.tip')" />
                                    <span>{{ ratios.pb | numberDisplayFloat(3) }}</span>
                                </div>
                            </span>
                            <div class="row-align">
                                <div class="row-align">
                                    <tooltip :isText="$t('stocks.current_eps')" :msg="$t('stocks.eps.tip')" />
                                    <span>{{ ratios.current_EPS | numberDisplay }} <span class="currencyFlag">{{
                                            $t('currency') }}</span>
                                    </span>
                                </div>
                                <div class="row-align">
                                    <tooltip :isText="$t('stocks.eps.title')" :msg="$t('stocks.eps.tip')" />
                                    <span>
                                        {{ ratios.eps | numberDisplay }} <span class="currencyFlag">{{ $t('currency') }}
                                        </span>
                                    </span>
                                </div>
                            </div>
                            <div class="row-align">
                                <div class="row-align">
                                    <tooltip :isText="$t('stocks.current_pe')" :msg="$t('stocks.pe.tip')" />
                                    <span>
                                        <index-status :status="ratios.r_pe"
                                            :value="ratios.current_PE | numberDisplayFloat()" />
                                    </span>
                                </div>
                                <div class="row-align">
                                    <tooltip :isText="$t('stocks.pe.title')" :msg="$t('stocks.pe.tip')" />
                                    <span>{{ ratios.pe | numberDisplayFloat() }}</span>
                                </div>
                            </div>
                            <div class="row-align">
                                <tooltip :isText="$t('stocks.pvgo.title')" :msg="$t('stocks.pvgo.tip')" />
                                <span>{{ ratios.pvgo | numberDisplayFloat(0) }} <span class="currencyFlag">{{
                                    $t('currency') }}</span>
                                </span>
                            </div>
                            <div class="row-align">
                                <div class="row-align">
                                    <tooltip :isText="$t('stocks.roe')" :msg="$t('stocks.roe')" />
                                    <span>{{ ratios.roe | percentDisplay }}
                                    </span>
                                </div>
                                <div class="row-align">
                                    <tooltip :isText="$t('stocks.reinvestment_rate.title')"
                                        :msg="$t('stocks.reinvestment_rate.tip')" />
                                    <span>{{ ratios.reinvestment_rate | percentDisplay() }}</span>
                                </div>
                            </div>
                            <div class="row-align">
                                <div class="row-align">
                                    <small class="text-muted">{{ $t('dividend.title') }}</small>
                                    <span>{{ ratios.dividend | percentDisplay() }}</span>
                                </div>
                                <div class="row-align">
                                    <small class="text-muted">{{ $t('dividend.average_title') }}</small>
                                    <span>{{ ratios.avgDividend | percentDisplay() }}</span>
                                </div>
                            </div>
                            <div class="row-align">
                                <div class="row-align">
                                    <small class="text-muted">{{ $t('payout_ratio.title') }}</small>
                                    <span>{{ ratios.payout_ratio | percentDisplay() }}</span>
                                </div>
                                <div class="row-align">
                                    <small class="text-muted">{{ $t('payout_ratio.average_title') }}</small>
                                    <span>{{ ratios.avg_payout_ratio | percentDisplay() }}</span>
                                </div>
                            </div>
                            <div class="row-align">
                                <div class="row-align">
                                    <tooltip :isText="$t('stocks.peg.title')" :msg="$t('stocks.peg.tip')" />
                                    <span><index-status :status="ratios.r_peg"
                                            :value="ratios.peg | numberDisplayFloat()" />
                                    </span>
                                </div>
                                <div class="row-align">
                                    <tooltip :isText="$t('stocks.market_capital.title')"
                                        :msg="$t('stocks.market_capital.tip')" />
                                    <span><index-status :status="ratios.r_market_capital"
                                            :value="ratios.market_capital | numberDisplay" />
                                    </span>
                                </div>
                            </div>
                            <div class="row-align">
                                <div class="row-align">
                                    <small class="text-muted">{{ $t('stocks.beta') }}</small>
                                    <span>{{ ratios.beta }}</span>
                                </div>
                                <div class="row-align">
                                    <tooltip :isText="$t('stocks.has_treasury_stock.title')"
                                        :msg="$t('stocks.has_treasury_stock.tip')" />
                                    <span>
                                        <!-- <i class="fas fa-check"></i> -->
                                        <i :class="{'fas fa-check text-positive' : ratios.has_treasury_stock === true, 'fas fa-times text-negative' : ratios.has_treasury_stock === false}"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </loading-container>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import LoadingContainer from "@/components/Misc/LoadingContainer";
import IconTooltip from "../../../components/Common/IconTooltip.vue";
import Tooltip from "@/components/Common/Tooltip";
import ChangeStatus from "@/components/Common/ChangeStatus";
import IndexStatus from '@/components/Common/IndexStatus';
import common from "@/libs/common";

export default {
    components: {
        LoadingContainer,
        IconTooltip,
        Tooltip,
        ChangeStatus,
        IndexStatus
    },
    props: {
        symbol: {
            type: String,
            default: ""
        }
    },
    beforeMount() {
        this.getStockInfo(this.symbol);
    },
    computed: {
        ...mapGetters({
            form: "stock/form",
            ratios: "stock/ratios"
        }),

    },
    methods: {
        ...mapActions({
            getStockInfo: "stock/getStockInfo",
            setField: "stock/setField",
        }),
        getTypeIcons(code) {
            return common.getTypeIcons(code);
        },
        getSectorIcons(code) {
            return common.getSectorIcons(code);
        },
        getSectorText(code) {
            return common.getSectorText(code);
        },
        getBusinessTypeText(code) {
            return common.getBusinessTypeText(code);
        },
        formattedDescription() {
            if (this.form.toggleDes) {
                return this.ratios.desc;
            }
            return this.ratios.desc.length > 100 ? `${this.ratios.desc.slice(0, 100).trim()}...` : 'N/A';
        },
        collapseSidebar(key) {
            this.setField({ key: key, value: !this.form[key] });
        },
        closePanel() {
            this.$emit('close');
        }
    }
}

</script>